<template>
	<spinner v-if="!errorCode" />
	<error-card
		v-else
		:code="errorCode"
		:message="errorMessage"
		:app-info="appInfo"
		title="Sign in failed"
		:email="errorEmail"
	>
		<template v-if="appInfo.app_name === 'device-agent'" #logo>
			<img src="/images/hp-device-agent-logo.svg" alt="HP" />
		</template>
		<template v-else-if="appInfo.app_name === 'brand-centre'" #logo>
			<img src="/images/hp-brand-centre-logo.svg" alt="HP" />
		</template>
		<template v-else-if="appInfo.app_name === 'control-centre'" #logo>
			<img src="/images/hp-control-centre-logo.svg" alt="HP" />
		</template>
		<template v-else-if="appInfo.app_name === 'workforce'" #logo>
			<img src="/images/hp-workforce-logo.svg" alt="HP" />
		</template>
		<template v-else-if="appInfo.app_name === 'sfl'" #logo>
			<img src="/images/hp-sfl-logo.svg" alt="HP" />
		</template>
		<template v-else-if="appInfo.app_name === 'sf'" #logo>
			<img src="/images/hp-sf-logo.svg" alt="HP" />
		</template>
	</error-card>
</template>

<script type="text/javascript">
import _get from 'lodash.get';
import axios from 'axios';
import { mapGetters } from 'vuex';
import Spinner from '@/components/Spinner';
import ErrorCard from '@/components/ErrorCard';

export default {
	name: 'AuthorizePage',
	components: {
		Spinner,
		ErrorCard
	},
	data() {
		const { authBase, ssoRedirectUrl = `${window.location.origin}/authorize`, apps = {}, app } = window.$config;

		return {
			authBase,
			ssoRedirectUrl,
			loginPageBase: '',
			errorMessage: '',
			errorEmail: '',
			errorCode: 0,
			apps,
			app
		};
	},
	computed: {
		...mapGetters('lang', ['lang']),
		appInfo() {
			const { app_name, port = 54322, domain: queryDomain = 'siteflow', state } = this.$route.query || {};
			const domain = queryDomain !== 'printos' ? 'siteflow' : 'printos';
			let stateFromUrl = {};
			try {
				stateFromUrl = JSON.parse(atob(state));
			} catch (e) {
				stateFromUrl = {};
			}
			const appInfo = { app_name, port, domain, ...stateFromUrl };
			Object.assign(appInfo, this.apps[appInfo.app_name], this.app);
			return appInfo;
		},
		isHPID() {
			return this.appInfo.sso === 'hpid';
		},
		hpEndpoint() {
			if (this.isHPID) {
				return `${this.loginPageBase}/authorize?response_type=code&client_id=${this.appInfo.clientId}&redirect_uri=${this.ssoRedirectUrl}&scope=email+openid+user.profile.read`;
			} else {
				return `${this.loginPageBase}/as/authorization.oauth2?response_type=code&client_id=${this.appInfo.clientId}&redirect_uri=${this.ssoRedirectUrl}&scope=email+openid+profile`;
			}
		}
	},
	async created() {
		const { code: hpCode, state } = this.$route.query || {};
		await this.getClientConfig();
		if (this.errorCode) return;
		const hasRedirectedFromSSO = hpCode && (state || !this.isHPID);
		if (hasRedirectedFromSSO) {
			try {
				const { token } = await this.getUserToken(hpCode);
				// Goto GSB App the user was trying to log in to.
				this.gotoApp(token, hasRedirectedFromSSO);
			} catch (err) {
				const { code, message, data = {} } = _get(err, 'response.data', {});
				if (code === 400 && message === 'The provided authorization code is expired or invalid') {
					this.gotoLoginPage();
					return this.$router.replace({ query: {} });
				} else if (code === 400 && message === 'HP employees should login with ONEHP and not local accounts') {
					this.errorMessage = message;
					this.errorCode = 1007;
				} else {
					if (data.email) {
						this.errorEmail = data.email;
					}
					this.errorMessage = message;
					this.errorCode = 1005;
				}
			}
		} else if (this.appInfo.appName) {
			return this.gotoLoginPage();
		} else {
			this.errorCode = 1001;
		}
	},
	methods: {
		async getUserToken(hpCode) {
			const url = `${this.authBase}/sso/token`;
			const payload = {
				clientName: this.appInfo.app_name,
				code: hpCode,
				domain: this.appInfo.domain,
				redirectUrl: this.ssoRedirectUrl
			};
			const {
				data: { token }
			} = await axios.post(url, payload);
			return { token };
		},

		async getClientConfig() {
			try {
				if (!this.appInfo.app_name || !this.appInfo.appName) throw new Error();
				const { data: clientConfig } = await axios.get(`${this.authBase}/sso/client/${this.appInfo.app_name}`);
				if (!clientConfig || !clientConfig.clientId || !clientConfig.redirectUrl) throw new Error();

				this.loginPageBase = clientConfig.loginPageUrl;
				this.appInfo.clientId = clientConfig.clientId;
				this.appInfo.sso = clientConfig.sso;

				// Set the redirectUrl based on the client config. The url of the device agent needs to have included the port
				this.appInfo.redirectUrl =
					location.hostname === 'localhost' ? 'http://localhost:8080/#/' : clientConfig.redirectUrl;
				if (this.appInfo.app_name === 'device-agent') {
					this.appInfo.redirectUrl = `${clientConfig.redirectUrl}:${this.appInfo.port}/#/`;
				}
				if (!this.isHPID) {
					// OneUID doesn't support state as a query parameter like HPID does. We need to add a specific query param and then
					// add the whole redirect URL including the query param in the whitelist of the client's URLs in the OneUID portal
					this.ssoRedirectUrl = `${this.ssoRedirectUrl}?app_name=${this.appInfo.app_name}`;
				}
			} catch (err) {
				this.errorCode = 1001;
			}
		},

		gotoLoginPage() {
			let additionalQueryParams = '';
			if (this.isHPID) {
				// use state only for HPID. OneUID doesn't support state as a query parameter, but if it does in the future, use it as well
				const state = { app_name: this.appInfo.app_name };
				if (state.app_name === 'device-agent')
					Object.assign(state, { port: this.appInfo.port, domain: this.appInfo.domain });
				const encodedState = btoa(JSON.stringify(state));
				additionalQueryParams = `&state=${encodedState}&locale=${this.lang}`;
			}
			window.location.href = `${this.hpEndpoint}${additionalQueryParams}`;
		},

		gotoApp(token, hasRedirectedFromSSO) {
			if (hasRedirectedFromSSO) {
				try {
					window.location.href = `${this.appInfo.redirectUrl}?token=${token}`;
				} catch (e) {
					this.errorCode = 1003;
				}
			} else {
				this.errorCode = 1002;
			}
		}
	}
};
</script>

<style lang="scss">
img {
	height: 40px !important;
}
.AuthorizePage {
	height: 100vh;
	width: 100vw;

	> .row.justify-content-center {
		height: 100%;
		> .col-sm-6.col-lg-4 {
			align-self: center;
		}
	}
}
</style>
